import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  CalendarDays,
  CalendarClock,
  Check,
  ChevronDown,
  Lock,
  Pencil,
  Target,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrettyDate } from "@/lib/app-types";
import { RippleButton } from "./ripple-button";
import {
  chapterHealth,
  chapterProgress,
  healthClasses,
  plannedTarget,
  questionStats,
  nextPracticeDate,
  remarkClasses,
  remarkFor,
  taskState,
  type QuestionChapter,
  type QuestionTask,
} from "@/lib/question-plan";

type Props = {
  chapter: QuestionChapter;
  today: string;
  index: number;
  busy?: boolean;
  onComplete: (task: QuestionTask, target: number) => void;
  onEditTarget: (chapter: QuestionChapter) => void;
  onDelete: (chapter: QuestionChapter) => void;
  onReschedule?: (task: QuestionTask) => void;
};

export function QuestionChapterCard({
  chapter,
  today,
  index,
  busy,
  onComplete,
  onEditTarget,
  onDelete,
  onReschedule,
}: Props) {
  const [open, setOpen] = useState(false);
  const health = chapterHealth(chapter, today);
  const progress = chapterProgress(chapter);
  const next = nextPracticeDate(chapter, today);
  const todayTask = chapter.tasks.find((t) => taskState(t, today) === "today") ?? null;
  const overdue = chapter.tasks.filter((t) => taskState(t, today) === "overdue");
  const actionTask = todayTask ?? overdue[0] ?? null;
  const actionTarget = actionTask ? plannedTarget(chapter, actionTask, today) : 0;
  const stats = questionStats(chapter);
  const lastRemark = [...chapter.tasks]
    .filter((t) => t.status === "completed")
    .map(remarkFor)
    .filter(Boolean)
    .pop();

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.24), type: "spring", stiffness: 260, damping: 26 }}
      className="glass rounded-[24px] p-5"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-bold tracking-tight">{chapter.chapter}</h3>
          {chapter.topic ? (
            <p className="truncate text-[11px] text-muted-foreground">{chapter.topic}</p>
          ) : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-hairline bg-surface px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {chapter.source}
            </span>
            <span className="rounded-full border border-hairline bg-surface px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {chapter.scheduleMode === "self" ? "Self schedule" : "Auto schedule"}
            </span>
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                healthClasses(health.tone),
              )}
            >
              {health.emoji} {health.label} · {health.percent}%
            </span>
            {lastRemark ? (
              <span
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                  remarkClasses(lastRemark.tone),
                )}
              >
                {lastRemark.emoji} {lastRemark.label}
              </span>
            ) : null}
          </div>
        </div>
        <button
          onClick={() => onEditTarget(chapter)}
          aria-label="Edit question target"
          className="rounded-full bg-secondary/60 p-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          onClick={() => onDelete(chapter)}
          aria-label="Delete chapter"
          className="rounded-full bg-secondary/60 p-2 text-muted-foreground transition-colors hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      <div className="mt-4">
        <div className="h-2 overflow-hidden rounded-full bg-surface">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress.percent}%` }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="h-full rounded-full [background-image:var(--gradient-brand)]"
          />
        </div>
        <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
          {[
            { label: "Target", value: chapter.targetQuestions },
            { label: "Solved", value: progress.solved },
            { label: "Remaining", value: progress.remaining },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-hairline bg-surface py-2">
              <p className="text-sm font-extrabold tabular-nums">{stat.value}</p>
              <p className="text-[10px] text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-4 gap-2 text-center">
          {[
            { label: "Attempted", value: stats.attempted },
            { label: "Correct", value: stats.correct },
            { label: "Incorrect", value: stats.incorrect },
            { label: "Accuracy", value: `${stats.accuracy}%` },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-hairline bg-surface py-2">
              <p className="text-[13px] font-extrabold tabular-nums">{stat.value}</p>
              <p className="text-[10px] text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        {actionTask ? (
          <RippleButton
            size="sm"
            variant={todayTask ? "gradient" : "glass"}
            disabled={busy}
            onClick={() => onComplete(actionTask, actionTarget)}
            className={cn("flex-1", !todayTask && "text-warning")}
          >
            <Target className="size-4" />
            {todayTask ? `Start Practice · ${actionTarget}` : `Complete Overdue · ${actionTarget}`}
          </RippleButton>
        ) : (
          <div className="flex flex-1 items-center gap-2 rounded-full bg-secondary/50 px-4 py-2.5 text-[13px] font-medium text-muted-foreground">
            {next ? (
              <>
                <Lock className="size-3.5" /> Available on {formatPrettyDate(next)}
              </>
            ) : (
              <>
                <Check className="size-3.5 text-success" /> Practice plan finished
              </>
            )}
          </div>
        )}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle schedule"
          className="rounded-full bg-secondary/60 p-2.5 text-muted-foreground"
        >
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 space-y-2 border-t border-hairline pt-4">
              {chapter.tasks.map((task) => {
                const state = taskState(task, today);
                const remark = remarkFor(task);
                const target = plannedTarget(chapter, task, today);
                return (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 rounded-2xl border border-hairline bg-surface px-3 py-2.5"
                  >
                    <span className="grid size-7 shrink-0 place-items-center rounded-full bg-secondary/70 text-[11px] font-bold">
                      Q{task.practiceNumber}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] font-semibold">
                        {target} questions
                        {target !== task.targetQuestions ? (
                          <span className="ml-1 text-warning">(carried forward)</span>
                        ) : null}
                      </p>
                      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <CalendarDays className="size-3" /> Day {task.intervalDays} ·{" "}
                        {formatPrettyDate(task.dueDate)}
                      </p>
                    </div>
                    {chapter.scheduleMode === "self" && task.status === "pending" && onReschedule ? (
                      <button
                        onClick={() => onReschedule(task)}
                        aria-label="Reschedule session"
                        className="shrink-0 rounded-full bg-secondary/60 p-1.5 text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <CalendarClock className="size-3.5" />
                      </button>
                    ) : null}
                    {state === "completed" && remark ? (
                      <span
                        className={cn(
                          "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                          remarkClasses(remark.tone),
                        )}
                      >
                        {remark.emoji} {remark.label}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          state === "today" && "bg-primary/15 text-primary",
                          state === "overdue" && "bg-warning/15 text-warning",
                          state === "locked" && "bg-secondary/60 text-muted-foreground",
                        )}
                      >
                        {state === "today" ? "Today" : state === "overdue" ? "Overdue" : "Locked"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.article>
  );
}